import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Card
} from 'antd';
import passwordValidator from 'password-validator';
import { useHistory, useLocation } from 'react-router-dom';
import { GlobalApiErrorHandler } from '../../shared/helpers';
import { InputBox, isBrowser, MsgBox } from '../../shared/custom-ant-d'
import { reqPostUpdateCandidatePassword, reqPostValidateResetPasswordLink } from '../../axios/api';

const schema = new passwordValidator();

const layout = {
  labelCol: {
    span: 8,
  },
  wrapperCol: {
    span: 16,
  },
};
const tailLayout = {
  wrapperCol: {
    md: {
      offset: 8,
      span: 16,
    }
  },
};

const validateMessages = {
  required: '${label} is required!',
};

export const PasswordReset = () => {

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();
  const history = useHistory();
  const [candidateId, setCandidateId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [requestId, setRequestId] = useState("");

  useEffect(() => {
    setSubmitLoading(true)
    var encKey = query.get('encKey');

    if (encKey !== undefined) {
      encKey = encKey.replace(" ", "+");
      validatePasswordRequest(encKey);
      setRequestId(encKey);
    } else {
      return history.push('/');
    }
  }, []);

  const onFinish = (values) => {
    setSubmitLoading(true);
    // §11.3: send the plaintext password over TLS; Careers.Web encrypts it server-side.
    submit(values, values.password);
  };

  const submit = (values, plainPassword) => {
    var jsonData = {
      CandidateID: candidateId,
      Password: plainPassword,
      RequestID: requestId,
    }

    var jsonStr = JSON.stringify(jsonData);

    reqPostUpdateCandidatePassword(jsonStr)
      .then(res => {
        setSubmitLoading(false);
        if (res.data.isSuccess) {
          MsgBox({
            type: 'success',
            message: 'Success!',
            description: "Password successsfully changed. Please login using your new password.",
          });
        } else {
          MsgBox({
            type: 'error',
            message: 'Error',
            description: res.data.errorMessage
          });
        }


        history.push('/login');
      })
      .catch(err => {
        console.log(err);
        setSubmitLoading(false);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
        history.push('/');
      });
  }

  const validatePasswordRequest = (encKey) => {
    var jsonData = {
      Parameter: encKey
    };

    var jsonStr = JSON.stringify(jsonData);

    reqPostValidateResetPasswordLink(jsonStr)
      .then(res => {
        setSubmitLoading(false);

        if (!res.data.isSuccess) {
          MsgBox({
            type: 'error',
            message: 'Error',
            description: res.data.errorMessage,
          });

          return history.push('/');
        } else {
          setCandidateId(res.data.candidateID);
          setRequestId(res.data.requestID);
        }
      })
      .catch(err => {
        console.log(err);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
        history.push('/');
      });
  }

  return (
    <>
      <Row className="margin-to-nav">
        <Col sm={{ span: 18, offset: 2 }} xs={24}>
          <Card title="Password Reset" className="iqor-header">
            <Form {...layout} name="nest-messages" onFinish={onFinish} validateMessages={validateMessages}>

              <Form.Item
                name="password"
                label="Password"
                hasFeedback
                rules={[
                  {
                    required: true,
                  },
                  () => ({
                    validator(_, value) {
                      schema
                        .is().min(8);
                      if (!schema.validate(value, { list: true }).includes('min')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs to be at least 8 characters long.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .is().max(20);
                      if (!schema.validate(value, { list: true }).includes('max')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password must not be longer than 20 characters.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .has().uppercase();

                      if (!schema.validate(value, { list: true }).includes('uppercase')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 1 uppercase character.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .has().digits(2);

                      if (!schema.validate(value, { list: true }).includes('digits')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 2 numerical characters.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .has().symbols();

                      if (!schema.validate(value, { list: true }).includes('symbols')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 1 special character.')
                      }
                    }
                  }), () => ({
                    validator(_, value) {
                      schema
                        .has().not().spaces();

                      if (!schema.validate(value, { list: true }).includes('spaces')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password should not have spaces.')
                      }
                    }
                  }), () => ({
                    validator(_, value) {
                      schema
                        .has().lowercase();

                      if (!schema.validate(value, { list: true }).includes('lowercase')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 1 lowercase character.')
                      }
                    }
                  }),
                ]}
              >
                {
                  (isBrowser) ?
                    (<Input.Password />) :
                    (<InputBox type={'password'} />)
                }
              </Form.Item>

              <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your password!',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }

                      return Promise.reject('The two passwords that you entered do not match!');
                    },
                  }),
                ]}
              >
                {
                  (isBrowser) ?
                    (<Input.Password />) :
                    (<InputBox type={'password'} />)
                }
              </Form.Item>

              <Form.Item {...tailLayout}>
                <Button type="danger" htmlType="submit" loading={submitLoading} style={{ float: "right" }}>
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
